import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const TUTORIAL_COMPLETE_FIELD = 'tutorialCompleted';
const TUTORIAL_COMPLETED_AT_FIELD = 'tutorialCompletedAt';
const TUTORIAL_ELIGIBLE_FIELD = 'postOnboardingTutorialEligible';
const TUTORIAL_VERSION_FIELD = 'postOnboardingTutorialVersion';
const CURRENT_TUTORIAL_VERSION = 1;

export type PostOnboardingTutorialState = {
  eligible: boolean;
  completed: boolean;
  version: number | null;
};

/**
 * Returns tutorial eligibility + completion for the current user.
 * We intentionally only show this tutorial for *new users* (eligible=true).
 */
export async function getPostOnboardingTutorialState(userId: string): Promise<PostOnboardingTutorialState> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return { eligible: false, completed: false, version: null };
    }
    const data = userDoc.data();
    return {
      eligible: Boolean(data[TUTORIAL_ELIGIBLE_FIELD]),
      completed: Boolean(data[TUTORIAL_COMPLETE_FIELD]),
      version: typeof data[TUTORIAL_VERSION_FIELD] === 'number' ? (data[TUTORIAL_VERSION_FIELD] as number) : null,
    };
  } catch (error) {
    console.error('[tutorial] Error reading tutorial state:', error);
    return { eligible: false, completed: false, version: null };
  }
}

/**
 * Check if user has completed the post-onboarding tutorial
 */
export async function checkTutorialStatus(userId: string): Promise<boolean> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return false;
    }
    const data = userDoc.data();
    return Boolean(data[TUTORIAL_COMPLETE_FIELD]);
  } catch (error) {
    console.error('[tutorial] Error checking tutorial status:', error);
    // Fail-open: if we can't check, assume tutorial hasn't been completed
    return false;
  }
}

/**
 * Mark tutorial as complete in Firestore
 */
export async function markTutorialComplete(userId: string): Promise<void> {
  try {
    await setDoc(
      doc(db, 'users', userId),
      {
        [TUTORIAL_COMPLETE_FIELD]: true,
        [TUTORIAL_COMPLETED_AT_FIELD]: serverTimestamp(),
        // Once completed, user is no longer eligible to see the tutorial.
        [TUTORIAL_ELIGIBLE_FIELD]: false,
        [TUTORIAL_VERSION_FIELD]: CURRENT_TUTORIAL_VERSION,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('[tutorial] Error marking tutorial complete:', error);
    // Fail-open: don't throw, just log the error
  }
}

